import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Loader2,
  Mail,
  MessageCircle,
  Pencil,
  Phone,
  ShieldCheck,
  Trash2,
  UserCheck,
  X,
} from 'lucide-react';
import { api } from '@/lib/api';
import { useApi } from '@/hooks/useApi';
import { classNames, formatDate, formatDateTime } from '@/lib/utils';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import Modal from '@/components/ui/Modal';
import ActivityTimeline from '@/components/owners/ActivityTimeline';
import BotonLlamar from '@/components/centralita/BotonLlamar';
import { soportaCentralita } from '@/components/centralita/AnclaCCP';
import Estrellas from '@/components/candidatos/Estrellas';
import { ChipsCatalogo } from '@/components/candidatos/Chips';
import DocumentosCandidato from '@/components/candidatos/DocumentosCandidato';
import FormularioCandidato from '@/components/candidatos/FormularioCandidato';
import ModalEstado from '@/components/candidatos/ModalEstado';
import PanelIA from '@/components/candidatos/PanelIA';
import { useCatalogos } from '@/components/candidatos/catalogos';
import {
  CONSENTIMIENTO_LABELS,
  DEDICACION_LABELS,
  ESTADO_BADGES,
  ESTADO_LABELS,
  FUENTE_LABELS,
  IDIOMAS_LABELS,
  JORNADA_LABELS,
  MODALIDAD_LABELS,
  MOTIVO_DESCARTE_LABELS,
  NIVEL_IDIOMA_LABELS,
  PERMISO_TRABAJO_LABELS,
  TERNARIO_LABELS,
  etiqueta,
  nombreCompleto,
  plazoVencido,
  urlWhatsApp,
  VIA_RENOVACION_LABELS,
  type CambioEstadoCandidato,
  type Candidato,
  type EstadoCandidato,
  type ViaRenovacion,
} from '@/lib/candidatos';
import type { Activity, Task } from '@/lib/types';

/**
 * Ficha de un candidato.
 *
 * Pensada para leerse en el móvil de arriba abajo: contacto arriba, porque lo
 * primero que se hace con un candidato es llamarle; los datos y su CV en medio;
 * y los botones de decidir fijos abajo, que es donde queda el pulgar después de
 * haber leído.
 */

const SIGUIENTES: EstadoCandidato[] = [
  'en_bolsa',
  'preseleccionado',
  'entrevista',
  'contratado',
  'descartado',
];

/**
 * Un dato del perfil.
 *
 * `ancho="completo"` ocupa las dos columnas. Se usa en los campos cuyo valor es
 * una frase —zonas, idiomas, formación—; los que se contestan con «Sí» o con un
 * número van a media columna. En un teléfono eso es la diferencia entre catorce
 * datos en una tirada de 600 px y una tarjeta que cabe casi en una pantalla, y
 * lo que hay debajo —la actividad— sube otro tanto.
 */
function Dato({
  etiqueta: label,
  children,
  ancho = 'medio',
}: {
  etiqueta: string;
  children?: React.ReactNode;
  ancho?: 'medio' | 'completo';
}) {
  if (children === undefined || children === null || children === '') return null;
  return (
    <div className={ancho === 'completo' ? 'col-span-2' : undefined}>
      <dt className="text-xs uppercase tracking-wide text-sereno-dark/40">{label}</dt>
      <dd className="text-sm mt-0.5">{children}</dd>
    </div>
  );
}

export default function CandidatoDetallePage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { catalogos } = useCatalogos();

  const [candidato, setCandidato] = useState<Candidato | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [editando, setEditando] = useState(false);
  const [destino, setDestino] = useState<EstadoCandidato | null>(null);
  const [guardandoNota, setGuardandoNota] = useState(false);
  const [nota, setNota] = useState('');
  const [tipoNota, setTipoNota] = useState('note');
  const [renovando, setRenovando] = useState(false);
  const [preguntandoVia, setPreguntandoVia] = useState(false);
  const [creandoEmpleado, setCreandoEmpleado] = useState(false);
  const [borrando, setBorrando] = useState(false);
  const [confirmarBorrado, setConfirmarBorrado] = useState(false);
  const [nombreEscrito, setNombreEscrito] = useState('');

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      setCandidato(await api.getCandidato(id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error de conexión');
    } finally {
      setCargando(false);
    }
  }, [id]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  const { data: actividades, refresh: refrescarActividades } = useApi<Activity[]>(
    () => api.getActivities({ entityId: id, entityType: 'candidato' }),
    [id],
  );
  const { data: tareas, refresh: refrescarTareas } = useApi<Task[]>(
    () => api.getTasks({ relatedId: id }),
    [id],
  );

  /**
   * Mientras el modelo lee el CV, la ficha se va poniendo al día.
   *
   * La lectura arranca sola al subir el currículum y tarda entre veinte y
   * sesenta segundos, así que hay que volver a preguntar: no hay avisos del
   * servidor al navegador en esta aplicación. Cada cuatro segundos, y solo
   * mientras de verdad está leyendo.
   */
  useEffect(() => {
    if (candidato?.ia?.estado !== 'leyendo') return;
    const temporizador = setInterval(() => {
      api
        .getCandidato(id)
        .then((actualizado) => {
          setCandidato(actualizado);
          if (actualizado.ia?.estado !== 'leyendo') refrescarActividades();
        })
        .catch(() => {
          /* si falla una consulta, se prueba en la siguiente */
        });
    }, 4000);
    return () => clearInterval(temporizador);
  }, [candidato?.ia?.estado, id, refrescarActividades]);

  const guardarEdicion = async (datos: Record<string, unknown>) => {
    setCandidato(await api.actualizarCandidato(id, datos));
    setEditando(false);
    refrescarActividades();
  };

  const cambiarEstado = async (cambio: CambioEstadoCandidato) => {
    setCandidato(await api.cambiarEstadoCandidato(id, cambio));
    setDestino(null);
    refrescarActividades();
    // Preseleccionar y citar crean tarea: sin esto no aparece hasta recargar,
    // y quien acaba de preseleccionar quiere ver que la llamada tiene dueño.
    refrescarTareas();
  };

  const valorar = async (valor: number | null) => {
    setCandidato(await api.actualizarCandidato(id, { valoracion: valor }));
  };

  const anadirNota = async () => {
    if (!nota.trim()) return;
    setGuardandoNota(true);
    try {
      await api.registrarActividadCandidato(id, {
        activityType: tipoNota,
        description: nota.trim(),
      });
      setNota('');
      refrescarActividades();
    } finally {
      setGuardandoNota(false);
    }
  };

  /** Deja constancia de que se le ha llamado o escrito, sin teclear nada. */
  const registrarContacto = (tipo: 'call' | 'whatsapp') => {
    api
      .registrarActividadCandidato(id, { activityType: tipo })
      .then(() => refrescarActividades())
      .catch(() => {});
  };

  /**
   * Alarga la conservación otro plazo completo. Solo cuando el candidato lo ha
   * pedido: la política de privacidad promete 12 meses desde que nos llega el
   * CV, así que la vía por la que lo pidió queda como su nuevo consentimiento.
   */
  const renovar = async (via: ViaRenovacion) => {
    setRenovando(true);
    try {
      setCandidato(await api.renovarCandidato(id, via));
      setPreguntandoVia(false);
      refrescarActividades();
    } finally {
      setRenovando(false);
    }
  };

  /**
   * De candidato contratado a ficha de empleado.
   *
   * El servidor copia los datos y el CV, enlaza las dos fichas y da de alta a
   * la persona en la app de lavandería. El candidato NO se borra.
   */
  const crearFichaEmpleado = async () => {
    setCreandoEmpleado(true);
    try {
      const { empleado } = await api.crearEmpleadoDesdeCandidato(id);
      navigate(`/equipo/personal/${empleado.id}`);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'No se ha podido crear la ficha de empleado');
      setCreandoEmpleado(false);
    }
  };

  const borrar = async () => {
    setBorrando(true);
    try {
      await api.borrarCandidato(id);
      navigate('/equipo/candidatos');
    } finally {
      setBorrando(false);
    }
  };

  if (cargando) return <LoadingSpinner />;

  if (error || !candidato) {
    return (
      <div>
        <Link to="/equipo/candidatos" className="btn-ghost inline-flex items-center gap-2 text-sm mb-4">
          <ArrowLeft size={16} /> Candidatos
        </Link>
        <div className="card bg-red-50 border-red-200 text-red-700 text-sm">
          No se ha podido cargar la ficha: {error}
        </div>
      </div>
    );
  }

  const c = candidato;
  const nombre = nombreCompleto(c);
  // El softphone de Connect solo va en Chrome, Edge y Firefox de escritorio.
  const conCentralita = soportaCentralita();
  const whatsapp = urlWhatsApp(c);
  const vencido = plazoVencido(c) || !!c.caducado;

  return (
    <div className="pb-28 md:pb-0">
      <Link to="/equipo/candidatos" className="btn-ghost inline-flex items-center gap-2 text-sm mb-4">
        <ArrowLeft size={16} /> Candidatos
      </Link>

      {vencido && (
        <div className="card bg-amber-50 border-amber-200 mb-4 py-3">
          <p className="text-sm text-amber-900 flex items-start gap-2">
            <ShieldCheck size={16} className="mt-0.5 shrink-0" />
            <span>
              Su currículum ha cumplido el plazo de conservación ({formatDate(c.conservarHasta)}).
              Elimínalo, o renuévalo abajo si te ha pedido seguir en la bolsa.
            </span>
          </p>
        </div>
      )}

      {/* Cabecera. En el móvil «Editar» comparte línea con el nombre en vez de
          caer debajo: ahí se comía cuarenta píxeles de una pantalla que ya
          tiene que llegar hasta la actividad. */}
      <div className="flex items-start justify-between gap-2 mb-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-xl sm:text-2xl font-bold">{nombre}</h1>
            <span className={classNames('badge', ESTADO_BADGES[c.estado])}>
              {ESTADO_LABELS[c.estado]}
            </span>
          </div>
          <div className="flex items-center gap-3 mt-1.5">
            <Estrellas valor={c.valoracion} onChange={(v) => void valorar(v)} tamano={18} />
            <span className="text-sm text-sereno-dark/40">
              Alta el {formatDate(c.createdAt)} · {etiqueta(FUENTE_LABELS, c.fuente)}
            </span>
          </div>
          {c.puestos.length > 0 && (
            <div className="mt-2">
              <ChipsCatalogo ids={c.puestos} labels={catalogos.puestoLabel} />
            </div>
          )}
        </div>

        <button
          onClick={() => setEditando(true)}
          aria-label="Editar la ficha"
          className="btn-ghost h-11 px-3 sm:px-4 text-sm flex items-center gap-2 shrink-0"
        >
          <Pencil size={16} />
          <span className="hidden sm:inline">Editar</span>
        </button>
      </div>

      {/* Contacto: lo primero que se hace con un candidato es llamarle */}
      <div className="card mb-4">
        <h2 className="font-medium mb-3">Contacto</h2>
        <div className="flex flex-wrap gap-2">
          {c.telefono &&
            (conCentralita ? (
              /* Con centralita se marca desde el CRM, no con un `tel:` que
                 abriría el teléfono del escritorio. El número se deja a la
                 vista —hay que poder leerlo y copiarlo— y al lado el botón que
                 llama de verdad, el mismo de Leads y del historial. */
              <div className="flex items-center gap-1 h-11 pl-3 pr-1 rounded-lg border border-gray-200 bg-white">
                <Phone size={15} className="text-sereno-dark/40 shrink-0" />
                <span className="text-sm">{c.telefono}</span>
                <BotonLlamar
                  numero={c.telefono}
                  nombre={nombre}
                  alojamiento={c.localidad || null}
                  onLlamada={() => registrarContacto('call')}
                />
              </div>
            ) : (
              /* En el móvil y en Safari no hay centralita —el softphone de
                 Connect es solo escritorio—, así que se marca con el teléfono
                 del propio aparato, que además es lo natural ahí. */
              <a
                href={`tel:${c.telefono}`}
                onClick={() => registrarContacto('call')}
                className="btn-primary flex-1 sm:flex-none min-w-[8rem] flex items-center justify-center gap-2 h-11 text-sm"
              >
                <Phone size={16} className="shrink-0" />
                <span className="truncate">{c.telefono}</span>
              </a>
            ))}
          {whatsapp && (
            <a
              href={whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => registrarContacto('whatsapp')}
              className="btn-ghost flex-1 sm:flex-none min-w-[8rem] flex items-center justify-center gap-2 h-11 text-sm"
            >
              <MessageCircle size={16} /> WhatsApp
            </a>
          )}
          {c.email && (
            <a
              href={`mailto:${c.email}`}
              className="btn-ghost w-full sm:w-auto flex items-center justify-center gap-2 h-11 text-sm min-w-0"
            >
              <Mail size={16} className="shrink-0" />
              <span className="truncate">{c.email}</span>
            </a>
          )}
          {!c.telefono && !c.email && (
            <p className="text-sm text-sereno-dark/40">Esta ficha no tiene teléfono ni correo.</p>
          )}
        </div>
      </div>

      {/* La actividad, a lo ancho y justo debajo del contacto: es lo que se
          toca después de cada llamada, y estaba al fondo del todo debajo de
          tres paneles. Aquí cae a un pulgar de distancia en el móvil, y en
          pantalla grande la caja de escribir tiene sitio de sobra. */}
      <div className="card mb-4">
        <h2 className="font-medium mb-3">Actividad</h2>

        <div className="mb-4 space-y-2">
          <div className="flex gap-1.5">
            {[
              { id: 'note', label: 'Nota' },
              { id: 'call', label: 'Llamada' },
              { id: 'whatsapp', label: 'WhatsApp' },
            ].map((t) => (
              <button
                key={t.id}
                onClick={() => setTipoNota(t.id)}
                aria-pressed={tipoNota === t.id}
                className={classNames(
                  'h-9 px-3 rounded-lg border text-xs font-medium transition-colors',
                  tipoNota === t.id
                    ? 'border-sereno-dark bg-sereno-dark text-white'
                    : 'border-gray-200 bg-white text-sereno-dark/60 hover:border-sereno-dark/40',
                )}
              >
                {t.label}
              </button>
            ))}
          </div>
          <textarea
            className="input resize-none"
            rows={2}
            value={nota}
            onChange={(e) => setNota(e.target.value)}
            placeholder="Qué ha pasado con esta persona."
          />
          <button
            onClick={() => void anadirNota()}
            disabled={guardandoNota || !nota.trim()}
            className="btn-secondary h-10 text-sm flex items-center gap-2"
          >
            {guardandoNota && <Loader2 size={14} className="animate-spin" />}
            Añadir
          </button>
        </div>

        <ActivityTimeline activities={actividades || []} />
        </div>

      {/*
        Dos columnas a partir de `lg`, y en el móvil una sola en este mismo
        orden: a la izquierda lo que se lee sobre la persona —quién es, en qué
        punto está, qué tareas tiene— y a la derecha su documentación.

        Protección de datos va la última: es lo que menos se mira y lo que
        conviene tener lejos del pulgar, porque ahí está el botón de eliminar.
      */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
        <div className="space-y-4">
          {/* Perfil */}
          <div className="card">
            <h2 className="font-medium mb-3">Perfil</h2>
            <dl className="grid grid-cols-2 gap-x-3 gap-y-4">
              <Dato etiqueta="Dónde vive">{c.localidad}</Dato>
              <Dato etiqueta="Zonas donde puede trabajar" ancho="completo">
                {c.zonas?.length
                  ? c.zonas.map((z) => catalogos.zonaLabel[z] || z).join(', ')
                  : 'Cualquiera'}
              </Dato>
              <Dato etiqueta="Vehículo propio">{TERNARIO_LABELS[c.vehiculo]}</Dato>
              <Dato etiqueta="Carnet">{TERNARIO_LABELS[c.carnet]}</Dato>
              <Dato etiqueta="Permiso de trabajo">
                {c.permisoTrabajo ? PERMISO_TRABAJO_LABELS[c.permisoTrabajo] : undefined}
              </Dato>
              <Dato etiqueta="Años de experiencia">{c.experienciaAnios}</Dato>
              <Dato etiqueta="Dedicación">
                {DEDICACION_LABELS[c.disponibilidad?.dedicacion]}
              </Dato>
              <Dato etiqueta="Disponible desde">
                {c.disponibilidad?.desde ? formatDate(c.disponibilidad.desde) : undefined}
              </Dato>
              <Dato etiqueta="Jornada" ancho="completo">
                {c.disponibilidad?.jornada?.length
                  ? c.disponibilidad.jornada.map((j) => JORNADA_LABELS[j]).join(', ')
                  : undefined}
              </Dato>
              <Dato etiqueta="Pretensión salarial">{c.pretensionSalarial}</Dato>
              <Dato etiqueta="Idiomas" ancho="completo">
                {c.idiomas?.length
                  ? c.idiomas
                      .map(
                        (i) =>
                          `${IDIOMAS_LABELS[i.codigo] || i.codigo} (${NIVEL_IDIOMA_LABELS[i.nivel]})`,
                      )
                      .join(', ')
                  : undefined}
              </Dato>
              <Dato etiqueta="Formación" ancho="completo">{c.formacion}</Dato>
              <Dato etiqueta="Cómo nos llegó" ancho="completo">
                {c.fuenteDetalle
                  ? `${etiqueta(FUENTE_LABELS, c.fuente)} — ${c.fuenteDetalle}`
                  : etiqueta(FUENTE_LABELS, c.fuente)}
              </Dato>
              <Dato etiqueta="Motivo del descarte" ancho="completo">
                {c.motivoDescarte ? MOTIVO_DESCARTE_LABELS[c.motivoDescarte] : undefined}
              </Dato>
            </dl>

            {(c.experiencia || c.notas || c.etiquetas?.length > 0) && (
              <div className="mt-4 pt-4 border-t border-gray-100 space-y-3">
                {c.experiencia && (
                  <div>
                    <dt className="text-xs uppercase tracking-wide text-sereno-dark/40 mb-1">
                      Experiencia
                    </dt>
                    <p className="text-sm whitespace-pre-wrap">{c.experiencia}</p>
                  </div>
                )}
                {c.notas && (
                  <div>
                    <dt className="text-xs uppercase tracking-wide text-sereno-dark/40 mb-1">
                      Notas internas
                    </dt>
                    <p className="text-sm whitespace-pre-wrap">{c.notas}</p>
                  </div>
                )}
                {c.etiquetas?.length > 0 && (
                  <div>
                    <dt className="text-xs uppercase tracking-wide text-sereno-dark/40 mb-1">
                      Etiquetas
                    </dt>
                    <ChipsCatalogo ids={c.etiquetas} labels={{}} />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Entrevista y contratación */}
          {(c.entrevista || c.contratacion) && (
            <div className="card">
              <h2 className="font-medium mb-3">Proceso</h2>
              <dl className="space-y-3">
                {c.entrevista && (
                  <Dato etiqueta="Entrevista">
                    {formatDateTime(c.entrevista.fecha)} ·{' '}
                    {MODALIDAD_LABELS[c.entrevista.modalidad]}
                    {c.entrevista.lugar && ` · ${c.entrevista.lugar}`}
                  </Dato>
                )}
                {c.contratacion && (
                  <Dato etiqueta="Contratación">
                    Se incorpora el {formatDate(c.contratacion.fecha)}
                    {c.contratacion.puesto &&
                      ` como ${catalogos.puestoLabel[c.contratacion.puesto] || c.contratacion.puesto}`}
                    {c.contratacion.nota && ` — ${c.contratacion.nota}`}
                  </Dato>
                )}
              </dl>
            </div>
          )}

          {/* Tareas */}
          {tareas && tareas.length > 0 && (
            <div className="card">
              <h2 className="font-medium mb-3">Tareas</h2>
              <ul className="space-y-2">
                {tareas.map((t) => (
                  <li key={t.id} className="flex items-start gap-2 text-sm">
                    <span
                      className={classNames(
                        'mt-1.5 h-2 w-2 rounded-full flex-shrink-0',
                        t.status === 'completed' ? 'bg-green-500' : 'bg-amber-500',
                      )}
                    />
                    <div className="min-w-0">
                      <p className={t.status === 'completed' ? 'line-through opacity-60' : ''}>
                        {t.title}
                      </p>
                      {t.dueDate && (
                        <p className="text-xs text-sereno-dark/40">
                          Para el {formatDateTime(t.dueDate)}
                        </p>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="space-y-4">
          <DocumentosCandidato candidato={c} onActualizado={setCandidato} />

          <PanelIA candidato={c} onActualizado={setCandidato} />

          {/* El puente con Personal: de candidato contratado a ficha de empleado */}
          {c.estado === 'contratado' && (
            <div className="card border border-green-200 bg-green-50/50">
              <h2 className="font-medium mb-1 flex items-center gap-2">
                <UserCheck size={17} className="text-green-600" />
                Contratado
              </h2>
              {(c as any).empleadoId ? (
                <>
                  <p className="text-sm text-sereno-dark/60 mb-3">
                    Ya tiene ficha de empleado en Personal, con su contrato, sus documentos y sus ausencias.
                  </p>
                  <Link to={`/equipo/personal/${(c as any).empleadoId}`} className="btn-secondary text-sm inline-block">
                    Abrir su ficha de empleado
                  </Link>
                </>
              ) : (
                <>
                  <p className="text-sm text-sereno-dark/60 mb-3">
                    Créale la ficha de empleado y se copian sus datos y su currículum. Esta ficha de candidato
                    no se borra: es la memoria de cómo llegó.
                  </p>
                  <button
                    onClick={() => void crearFichaEmpleado()}
                    disabled={creandoEmpleado}
                    className="btn-primary text-sm flex items-center gap-2 disabled:opacity-50"
                  >
                    {creandoEmpleado && <Loader2 size={14} className="animate-spin" />}
                    Crear ficha de empleado
                  </button>
                </>
              )}
            </div>
          )}

          {/* Protección de datos */}
          <div className="card">
            <h2 className="font-medium mb-3 flex items-center gap-2">
              <ShieldCheck size={17} className="text-sereno-dark/50" />
              Protección de datos
            </h2>
            <dl className="space-y-3">
              <Dato etiqueta="Consentimiento">
                {CONSENTIMIENTO_LABELS[c.consentimiento?.fuente || 'no_consta']}
                {c.consentimiento?.at && ` · ${formatDate(c.consentimiento.at)}`}
              </Dato>
              <Dato etiqueta="Se conserva hasta">
                {c.estado === 'contratado'
                  ? 'Sin plazo: está contratado y su documentación es laboral'
                  : formatDate(c.conservarHasta)}
              </Dato>
            </dl>

            {preguntandoVia && (
              <div className="mt-4 pt-3 border-t border-gray-100">
                <p className="text-sm text-sereno-dark/70 mb-2">
                  ¿Por dónde te pidió seguir en la bolsa? Se guarda como su consentimiento y el
                  plazo vuelve a empezar desde hoy.
                </p>
                <div className="flex flex-wrap items-center gap-1.5">
                  {(Object.keys(VIA_RENOVACION_LABELS) as ViaRenovacion[]).map((via) => (
                    <button
                      key={via}
                      onClick={() => void renovar(via)}
                      disabled={renovando}
                      className="h-9 px-3 rounded-lg border border-gray-200 bg-white text-xs font-medium text-sereno-dark/70 hover:border-sereno-dark/40 disabled:opacity-50"
                    >
                      {VIA_RENOVACION_LABELS[via]}
                    </button>
                  ))}
                  <button
                    onClick={() => setPreguntandoVia(false)}
                    disabled={renovando}
                    className="btn-ghost h-9 text-xs"
                  >
                    Cancelar
                  </button>
                  {renovando && <Loader2 size={14} className="animate-spin text-sereno-dark/50" />}
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-2 mt-4 pt-3 border-t border-gray-100">
              {c.estado !== 'contratado' && !preguntandoVia && (
                <button
                  onClick={() => setPreguntandoVia(true)}
                  className="btn-ghost h-10 text-sm"
                >
                  Pidió seguir en la bolsa
                </button>
              )}
              <button
                onClick={() => {
                  setNombreEscrito('');
                  setConfirmarBorrado(true);
                }}
                className="h-10 px-4 rounded-lg border border-red-200 text-red-600 bg-white hover:bg-red-50 text-sm font-medium flex items-center gap-2"
              >
                <Trash2 size={14} /> Eliminar definitivamente
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Decidir: fijo abajo en móvil, que es donde queda el pulgar */}
      <div className="fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-gray-200 p-3 lg:ml-56 md:static md:border-0 md:bg-transparent md:p-0 md:mt-4 md:z-auto">
        <div className="flex gap-2 overflow-x-auto scrollbar-none">
          {SIGUIENTES.filter((e) => e !== c.estado).map((e) => (
            <button
              key={e}
              onClick={() => setDestino(e)}
              className={classNames(
                'h-12 md:h-11 px-4 rounded-xl border text-sm font-medium whitespace-nowrap transition-colors flex-shrink-0',
                e === 'descartado'
                  ? 'border-red-200 text-red-600 bg-white hover:bg-red-50'
                  : e === 'contratado'
                    ? 'border-green-200 text-green-700 bg-white hover:bg-green-50'
                    : 'border-gray-200 bg-white hover:border-sereno-dark/40',
              )}
            >
              {ESTADO_LABELS[e]}
            </button>
          ))}
        </div>
      </div>

      {/* Modales */}
      <Modal isOpen={editando} onClose={() => setEditando(false)} title="Editar candidato" size="lg">
        <FormularioCandidato
          candidato={c}
          onGuardar={guardarEdicion}
          onCancelar={() => setEditando(false)}
        />
      </Modal>

      {destino && (
        <ModalEstado
          candidato={c}
          destino={destino}
          onConfirmar={cambiarEstado}
          onCerrar={() => setDestino(null)}
        />
      )}

      <Modal
        isOpen={confirmarBorrado}
        onClose={() => !borrando && setConfirmarBorrado(false)}
        title="Eliminar definitivamente"
      >
        <div className="space-y-4">
          <p className="text-sm">
            Se borrarán la ficha de <strong>{nombre}</strong>, sus {c.documentos?.length || 0}{' '}
            documento{c.documentos?.length === 1 ? '' : 's'} y todo su historial. No se puede
            deshacer.
          </p>
          <div>
            <label className="label" htmlFor="confirmar-nombre">
              Escribe «{nombre}» para confirmar
            </label>
            <input
              id="confirmar-nombre"
              className="input h-11"
              value={nombreEscrito}
              onChange={(e) => setNombreEscrito(e.target.value)}
              autoComplete="off"
            />
          </div>
          <div className="flex gap-2 justify-end">
            <button
              onClick={() => setConfirmarBorrado(false)}
              disabled={borrando}
              className="btn-ghost h-11 text-sm"
            >
              Cancelar
            </button>
            <button
              onClick={() => void borrar()}
              disabled={borrando || nombreEscrito.trim() !== nombre}
              className="h-11 px-5 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-medium flex items-center gap-2 disabled:opacity-50"
            >
              {borrando ? <Loader2 size={16} className="animate-spin" /> : <X size={16} />}
              Eliminar
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
